// frontend/client/src/hooks/useAnalytics.js

import { useQuery } from '@tanstack/react-query';
import { analyticsService } from '../services/apiClient'; // Use our centralized service

// This custom hook is the single source of truth for all analytics data.
// It handles fetching, caching, loading states, and error handling automatically.
export function useAnalytics({ days = 7, platform = 'all' } = {}) {

  // --- High-Level KPI Summary ---
  const { 
    data: summaryData, 
    isLoading: isSummaryLoading, 
    error: summaryError,
    refetch: refetchSummary 
  } = useQuery({ 
    // Filters are part of the cache key — switching tabs re-fetches instead
    // of silently showing stale data from a previous selection.
    queryKey: ['analyticsSummary', days, platform],
    queryFn: async () => { 
      const response = await analyticsService.getSummary({ days, platform });
      if (!response.success) { throw new Error(response.error || 'Failed to fetch summary'); }
      // Backend returns { success, isDemo, summary, performanceOverTime } — no .data wrapper
      return response; 
    },
    staleTime: 5 * 60 * 1000 // Data is considered fresh for 5 minutes 
  });

  // --- Top Performing Posts ---
  const { 
    data: topPosts, 
    isLoading: isTopPostsLoading, 
    error: topPostsError,
    refetch: refetchTopPosts 
  } = useQuery({ 
    queryKey: ['analyticsTopPosts', days, platform],
    queryFn: async () => { 
      const response = await analyticsService.getTopPosts({ days, platform });
      if (!response.success) { throw new Error(response.error || 'Failed to fetch top posts'); }
      // Backend returns { success, isDemo, posts } — no .data wrapper
      return response.posts || []; 
    },
    staleTime: 15 * 60 * 1000 // This data changes less often, so we can cache it longer 
  });

  // Combine loading and error states for simpler use in the component
  const isLoading = isSummaryLoading || isTopPostsLoading;
  const error = summaryError || topPostsError;

  // Expose a single, simple refetch function to the component
  const refetchAll = () => { 
    refetchSummary();
    refetchTopPosts();
  };

  return {
    summaryData,
    topPosts,
    isLoading,
    error,
    refetchAll
  };
}